import { PointerEvent as ReactPointerEvent, memo } from "react"
import { Box, Typography } from "@mui/material"
import { StaffSchedule } from "../../interfaces"
import { DayColumn } from "../../model/period"
import { CellKey, ScheduleMap, cellKey } from "../../model/scheduleCells"
import { GridCell } from "./gridCell"
import {
  ROW_H,
  STAFF_COL_W,
  TOTAL_COL_W,
  Z,
  gridBorderColor,
  gridBorderColorStrong,
} from "./gridGeometry"

interface GridRowProps {
  staff: StaffSchedule.Staff
  days: DayColumn[]
  cells: ScheduleMap
  legend: Map<number, StaffSchedule.ShiftType>
  /** Превью мазка — не-null ровно у одной строки: мазок не выходит за свою строку. */
  strokeRange: { fromIdx: number; toIdx: number } | null
  pendingKeys: ReadonlySet<CellKey>
  total: number
  canWrite: boolean
  onCellPointerDown: (
    staffId: number,
    colIdx: number,
    e: ReactPointerEvent<HTMLElement>,
  ) => void
  onCellPointerEnter: (staffId: number, colIdx: number) => void
}

export const GridRow = memo(
  ({
    staff,
    days,
    cells,
    legend,
    strokeRange,
    pendingKeys,
    total,
    canWrite,
    onCellPointerDown,
    onCellPointerEnter,
  }: GridRowProps) => (
    <Box sx={{ display: "flex", height: ROW_H }}>
      <Box
        sx={{
          position: "sticky",
          left: 0,
          zIndex: Z.stickyCol,
          width: STAFF_COL_W,
          flex: "0 0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: 1,
          overflow: "hidden",
          bgcolor: "background.paper", // непрозрачный фон обязателен
          borderRight: "1px solid",
          borderBottom: "1px solid",
          borderColor: gridBorderColor,
          // отбивка залипшей колонки — линия темнее внутренних
          borderRightColor: gridBorderColorStrong,
        }}
      >
        <Typography variant="body2" noWrap lineHeight={1.2}>
          {staff.name}
        </Typography>
      </Box>

      {days.map((day, colIdx) => {
        const key = cellKey(staff.id, day.key)
        const schedule = cells[key]
        const inStroke =
          strokeRange !== null &&
          colIdx >= strokeRange.fromIdx &&
          colIdx <= strokeRange.toIdx
        return (
          <GridCell
            key={day.key}
            staffId={staff.id}
            colIdx={colIdx}
            day={day}
            schedule={schedule}
            shiftType={
              schedule ? legend.get(schedule.shift_type_id) : undefined
            }
            inStroke={inStroke}
            isStrokeStart={inStroke && colIdx === strokeRange?.fromIdx}
            isStrokeEnd={inStroke && colIdx === strokeRange?.toIdx}
            isPending={pendingKeys.has(key)}
            canWrite={canWrite}
            onPointerDown={onCellPointerDown}
            onPointerEnter={onCellPointerEnter}
          />
        )
      })}

      <Box
        data-testid={`total-staff-${staff.id}`}
        sx={{
          position: "sticky",
          right: 0,
          zIndex: Z.stickyCol,
          width: TOTAL_COL_W,
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          bgcolor: "background.paper",
          borderLeft: "1px solid",
          borderBottom: "1px solid",
          borderColor: gridBorderColor,
          borderLeftColor: gridBorderColorStrong,
        }}
      >
        {total}
      </Box>
    </Box>
  ),
)
GridRow.displayName = "GridRow"
